import { FSAktivitetfeilDto } from '../../api/generated/studiested-generated-api';
import { useFormatMessageFor } from '../../i18n/translation';
import translations from './fordeling.i18n';
import { useActivityContextUHA } from '../activitycontexts';
import PanelHeader from '../../components/panelheader/panelHeader';
import Colors from '../../alpColors';
import {
  ListItem,
  OrderedList,
  UnorderedList,
} from '../../components/list/list';
import React, { ReactNode, useContext, useState } from 'react';
import * as css from './fordeling.module.scss';
import {
  OnStedHoverCard,
  StedType,
} from '../../components/hierarchy/onhoverhierarchyinfo';
import { groupBy } from '../../util';
import Button from '../../components/button';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import useAddErrorNotificationCallback from '../../hooks/useAddErrorNotificationCallback';
import { NotificationFunctionContext } from '../../components/notifications/notificationProvider';

const UKJENT = 'UKJENT';

export const groupByPraksissted = (
  feilDtos: FSAktivitetfeilDto[],
): { [praksisstedId: string]: FSAktivitetfeilDto[] } =>
  groupBy(feilDtos, (feil) => feil.praksisstedId || UKJENT);

interface FeilmeldingerFraFSProps {
  feilmeldingerPerPraksissted: {
    [praksisstedId: string]: FSAktivitetfeilDto[];
  };
}

const FeilmeldingerFraFS = ({
  feilmeldingerPerPraksissted,
}: FeilmeldingerFraFSProps) => {
  const labels = useFormatMessageFor(translations);

  const feilmeldingerPerPraksisstedToRender = Object.entries(
    feilmeldingerPerPraksissted,
  ).map(([key, feil]) => (
    <FeilmeldingerFraFsPerPraksissted aktivitetfeil={feil} key={key} />
  ));

  return (
    <>
      <PanelHeader
        title={labels.feilmeldingerFraFsHeader}
        color={Colors.mojo}
        collapsible
      >
        <OrderedList>{feilmeldingerPerPraksisstedToRender}</OrderedList>
      </PanelHeader>
      <PanelHeader
        title={labels.nyPubliseringHeader}
        subtitle={labels.nyPubliseringHelpText}
        color={Colors.mojo}
        collapsible
      >
        <RepublishToFsBtn />
      </PanelHeader>
    </>
  );
};

type FsAktivitetfeilRequired = Required<
  Pick<
    FSAktivitetfeilDto,
    'praksisstedkode' | 'praksisstedId' | 'praksisstednavn'
  >
>;

interface FeilmeldingerFraFsOnPraksisstedWrapperProps {
  children: ReactNode;
}

const FeilmeldingerFraFsOnPraksisstedWrapper = ({
  children,
}: FeilmeldingerFraFsOnPraksisstedWrapperProps) => (
  <ListItem>
    <div>{children}</div>
  </ListItem>
);

interface FeilmeldingerFraFsPerPraksisstedProps {
  aktivitetfeil: FSAktivitetfeilDto[];
}

const getFsAktivitetfeilRequiredUkjentPraksissted =
  (): FsAktivitetfeilRequired => ({
    praksisstednavn: UKJENT,
    praksisstedId: UKJENT,
    praksisstedkode: UKJENT,
  });

const FeilmeldingerFraFsPerPraksissted = ({
  aktivitetfeil,
}: FeilmeldingerFraFsPerPraksisstedProps) => {
  if (aktivitetfeil.length === 0) {
    return <></>;
  }

  const feilmeldinger = aktivitetfeil.map((feil) => (
    <FeilmeldingFraFS
      aktivitetfeil={feil}
      key={feil.feilmelding?.feilmeldingId}
    />
  ));

  const feilWithKodeIdAndNavn = (aktivitetfeil.find(
    (feil) =>
      feil.praksisstednavn && feil.praksisstedId && feil.praksisstedkode,
  ) ||
    getFsAktivitetfeilRequiredUkjentPraksissted()) as FsAktivitetfeilRequired;

  return (
    <FeilmeldingerFraFsOnPraksisstedWrapper>
      <FeilmeldingerPerPraksisstedLabel
        aktivitetfeilRequired={feilWithKodeIdAndNavn}
      />
      <UnorderedList>{feilmeldinger}</UnorderedList>
    </FeilmeldingerFraFsOnPraksisstedWrapper>
  );
};

interface FeilmeldingerPerPraksisstedLabelProps {
  aktivitetfeilRequired: FsAktivitetfeilRequired;
}

const FeilmeldingerPerPraksisstedLabel = ({
  aktivitetfeilRequired: { praksisstedkode, praksisstednavn, praksisstedId },
}: FeilmeldingerPerPraksisstedLabelProps) => {
  const { praksisstedLabel, ukjent } = useFormatMessageFor(translations);
  const isUkjent = praksisstedId === UKJENT;
  const title = isUkjent
    ? ukjent
    : `${praksisstedLabel}: ${praksisstedkode} - ${praksisstednavn}`;

  return (
    <div className={css.errorLabelWrapper}>
      <strong>{title}</strong>
      {!isUkjent && (
        <OnStedHoverCard
          sted={StedType.PRAKSISSTED}
          id={praksisstedId}
          navn={praksisstednavn}
        />
      )}
    </div>
  );
};

interface FeilmeldingFraFSProps {
  aktivitetfeil: FSAktivitetfeilDto;
}

const FeilmeldingFraFS = ({ aktivitetfeil }: FeilmeldingFraFSProps) => (
  <ListItem>{aktivitetfeil.feilmelding?.message}</ListItem>
);

const RepublishToFsBtn = () => {
  const {
    praksisaktivitet: { id },
  } = useActivityContextUHA();
  const { praksiskalenderApi } = useContext(StudiestedApiContext);
  const { addSuccessNotification } = useContext(NotificationFunctionContext);
  const errorNotificationCallback = useAddErrorNotificationCallback();

  const labels = useFormatMessageFor(translations);

  const [loading, setLoading] = useState(false);

  const onClick = () => {
    setLoading(true);
    praksiskalenderApi
      .praksisaktivitetIdPublishToFsPost({ pathParams: { id } })
      .then(() => {
        addSuccessNotification(labels.nyPubliseringSuccess);
      })
      .catch(errorNotificationCallback)
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Button.Secondary onClick={onClick} disabled={loading}>
      {`${labels.nyPubliseringBtn}${loading ? '...' : ''}`}
    </Button.Secondary>
  );
};

export default FeilmeldingerFraFS;
